// status-payload-types.ts
//
// Split out with status-operating-table-snapshot.ts (D12, phase 05 B1) so
// status-renderer.ts and status-operating-table-snapshot.ts can both
// depend on these base shapes without importing each other in a cycle.
import type { MissionState, TicketVerdictRecord } from "./mission-state-reducer.js";

export interface StatusPayload {
  mission_id: string | undefined;
  status: string | undefined;
  tickets: Record<string, string>;
  last_event_seq: number;
  store_status: "READY" | "RECOVERING";
  // Classifier verdict (category + redacted reasons) from each ticket's most
  // recent attempt_finished — added dogfooding P9b (mission ME-2026-c3985e
  // blocked with zero queryable reason anywhere: `status` only ever showed
  // "blocked", never why). Empty for a ticket that never had a non-done
  // outcome, or for older missions whose journal predates this field.
  ticket_last_verdict: Record<string, TicketVerdictRecord>;
}

/**
 * Structural subset both MissionStore (writer, EventJournal-lease-holding)
 * and MissionStoreReadOnly (Bug H fix — lock-free) satisfy. status-renderer
 * only ever READS `.state`/`.status`/`.controlRoot`, so it never needs the
 * concrete writer type — binding to this interface instead is what lets
 * readStatusSnapshot use the lock-free reader without status ever risking a
 * JournalLeaseError while `run` holds the writer lease.
 */
export interface ReadableMissionStore {
  readonly controlRoot: string;
  readonly state: MissionState;
  readonly status: "READY" | "RECOVERING";
}

/**
 * Build the machine-readable status payload from a MissionStore's projection.
 * Pure function over already-read state — unit-testable without I/O beyond
 * the store open the caller already did.
 */
export function buildStatusPayload(store: ReadableMissionStore): StatusPayload {
  const state = store.state;
  return {
    mission_id: state.mission_id,
    status: state.status,
    tickets: { ...state.tickets },
    last_event_seq: state.last_event_seq,
    store_status: store.status,
    ticket_last_verdict: { ...state.ticket_last_verdict },
  };
}
